/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from 'tracked-built-ins';

interface InputChipsSignature {
  Element: HTMLInputElement;
  Args: {
    /**
     * Special characters that should be used as item delimiters.
     * Current limitation: special RegEx characters are not allowed because
     * the default serialize method is relatively naive.
     **/
    separators?: string[];
    /** Parse the raw input string into individual items. */
    serialize?(value: string): string[];
    /** Validate an individual item. */
    validate?(item: string): boolean;
    /** Hook called when the items list changes; passed an up-to-date list all items. */
    onChange?(items: ChipToken[]): void;
  };
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    InputChips: typeof InputChips;
  }
}

export interface ChipToken {
  text: string;
  valid: boolean;
}

export class ChipItem {
  @tracked valid: boolean;
  text: string;
  constructor({ text, valid }: ChipToken) {
    this.text = text;
    this.valid = valid;
  }
  toObject(): ChipToken {
    return {
      text: this.text,
      valid: this.valid,
    };
  }
}

export default class InputChips extends Component<InputChipsSignature> {
  /**
   * Each item in this array represents an individual piece of text that
   * has been entered by the user. The raw user content is split up by
   * the `serializeContent` method, which defers to the `@serialize` argument,
   * and is represented as a ChipItem here.
   *
   * @see https://github.com/tracked-tools/tracked-built-ins
   */
  items: ChipItem[] = tracked([]);

  get separators(): string[] {
    return this.args.separators ?? [',', ' ', ';', ':'];
  }

  @action handleInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const { type, inputType } = event as InputEvent;

    // if we have well-formed input, process it and clear the input field
    if (this.#shouldProcessInputEvent(target, type, inputType)) {
      this.#processInput(target);
    }
  }

  @action handleBlur(event: FocusEvent): void {
    const target = event.target as HTMLInputElement;
    const { type } = event;

    if (this.#shouldProcessInputEvent(target, type)) {
      this.#processInput(target);
    }
  }

  /**
   * Handle Enter being pressed explicitly for the case where content is
   * entered into the input field that doesn't match the `this.separators`
   * and therefore doesn't trigger the processing of the content. This means
   * the `@onChange` event will not be fired and the parent `form` element's
   * submit event will trigger *without the content being parsed*.
   *
   * This approach allows the content to be parsed (and `@onChange` emitted
   * properly) while still allowing the default form submission behaviour
   * of the Enter key being pressed while focused within an `input` element.
   */
  @action handleEnter(
    event: KeyboardEvent & { target: HTMLInputElement }
  ): void {
    this.#processInput(event.target);
  }

  /**
   * For focus management and keyboard navigation.
   * Only applicable to Backspace and ArrowLeft; abort if anything else.
   * Then, focus the last chip item's remove button if and only if:
   * - the input field is empty
   * - there is already at least one chip rendered
   */
  @action handleInputKeyup(event: KeyboardEvent): void {
    const keys = ['Backspace', 'ArrowLeft'];
    if (!keys.includes(event.key)) return;

    const input = event.target as HTMLInputElement;
    const shouldFocusLastChip = input?.value === '' && this.items.length > 0;
    // focus the last chip's remove button
    if (shouldFocusLastChip) {
      const lastChip = input?.parentElement?.previousElementSibling;
      lastChip?.querySelector('button')?.focus();
    }
  }

  /**
   * Manage keyboard events when one of the chips is focused.
   * This covers deleting chips via Backspace as well as navigating
   * between chips with the ArrowLeft and ArrowRight.
   */
  @action handleChipKeyup(index: number, event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (event.key === 'Backspace') {
      this.removeItem(index, event);

      const previousChip = target.parentElement?.previousElementSibling;

      if (previousChip) {
        const btn = previousChip.querySelector('button');
        btn?.focus();
      } else {
        const container = target?.closest(
          '[data-chip-container]'
        ) as HTMLElement;
        const input = container?.querySelector(
          '[data-chip-input]'
        ) as HTMLInputElement;
        input?.focus();
      }
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      const method =
        event.key === 'ArrowRight'
          ? 'nextElementSibling'
          : 'previousElementSibling';
      const focusElement = target?.parentElement?.[method] as HTMLElement;

      if (!focusElement) return;

      if (focusElement.hasAttribute('data-chip-item')) {
        focusElement.querySelector('button')?.focus();
      } else {
        focusElement.querySelector('input')?.focus();
      }
    }
  }

  /** Remove a specific chip, based on index. */
  @action removeItem(
    index: number,
    event: PointerEvent | MouseEvent | KeyboardEvent
  ): void {
    // stop event from bubbling up and potentially triggering a click on a parent
    event.stopPropagation();

    // remove the item from the array
    this.items.splice(index, 1);

    this.#emitChange();
  }

  /**
   * Determine if the current contents of the input field are ready to be
   * processed. There are a number of rules here ranging from the type of input
   * event to the last character entered by the user.
   *
   * If this returns true, the current value of the input are processed and
   * converted into ChipItems to be rendered.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/InputEvent/inputType
   * @see https://d-toybox.com/studio/lib/input_event_viewer.html
   * @see https://rawgit.com/w3c/input-events/v1/index.html#interface-InputEvent-Attributes
   * @private
   */
  #shouldProcessInputEvent(
    target: HTMLInputElement,
    eventType: string, // values from InputEvent.type; e.g. 'blur' | 'input',
    inputType?: string // values from InputEvent.inputType
  ): boolean {
    // if last character is a separator, process the event
    if (
      this.separators.includes(target.value.substring(target.value.length - 1))
    ) {
      return true;
    }

    // always process content pasted into the input
    // note that this does not cover actual Clipboard 'paste' events.
    if (inputType === 'insertFromPaste') {
      return true;
    }

    // only process blur events if the input field has content
    if (eventType === 'blur' && target.value.length) {
      return true;
    }

    return false;
  }

  /**
   * Process the current value of the the input element.
   * This simply passes the unprocessed text content off to `serializeContent`
   * and appends the result to any already serialized content.
   *
   * It will automatically remove duplicate entries.
   *
   * To finish up, it clears out the input element and emits the change event.
   */
  #processInput(target: HTMLInputElement) {
    const newItems = this.#serializeContent(target.value)
      // skip text that are already in this.items
      .filter((text) => !this.items.find((x) => x.text === text))
      // convert to ChipItems
      .map((text) => new ChipItem({ text, valid: this.#validateItem(text) }));
    this.items.push(...newItems);

    target.value = '';
    this.#emitChange();
  }

  // _processBackspaceEvent(
  //   event: InputEvent & { target: HTMLInputElement }
  // ): boolean {
  //   const { target, inputType } = event;
  //   console.log(
  //     '_processBackspaceEvent',
  //     event.type,
  //     inputType,
  //     event.data,
  //     target,
  //     this.items
  //   );
  //   return target.value === '' && inputType === 'deleteContentBackward';
  // }

  /**
   * Parse the raw input string into individual items.
   * The default beahviour relies on args.separators and augments
   * it so that each separator item can also be followed by whitespace.
   *
   * Consumers can customize behaviour by overriding the `@separators` argument
   * or the `@serialize` argument to take full control and bypass the default
   * behaviour altogether.
   */
  #serializeContent(content: string): string[] {
    if (this.args.serialize) return this.args.serialize(content);

    // default serialization
    const useSpaceAsSeparator = this.separators.includes(' ');
    const separators = this.separators.filter((s) => s !== ' ');
    // @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Template_literals#raw_strings
    const augmentedSeparators = separators.map((s) => s + String.raw`\s`);
    const allSeparators = [
      useSpaceAsSeparator ? ' +' : null, // any amount of whitespace or null
      ...separators,
      ...augmentedSeparators,
    ].filter(Boolean);

    // If separators is `[',', ' ', ';', ':']`,
    // regex should look like this: `/ |,|;|:|,\s|;\s|:\s/g`
    const regex = new RegExp(allSeparators.join('|'), 'g');

    return content.split(regex).filter(Boolean);
  }

  /**
   * Validate an individual item. Defer to the passed in validate function if it exists.
   */
  #validateItem(item: string): boolean {
    return this.args.validate ? this.args.validate(item) : true;
  }

  /**
   * Notify the parent component when items have been added or removed.
   */
  #emitChange(): void {
    this.args.onChange?.(this.items.map((x) => x.toObject()));
  }
}
